import { ReservationState, RatePlanKey, CheckPriceResult, CalculatedPrice, CalculatedRowPrice, RoomBookingDateDto, RoomBlockDate, RoomBlockDateRoom, ReservationPerson, RoomBookingState, PersonDto, CompanyDto, RoomSplitMode, ProgramSplitMode, PriceBreakdownContainer, ReservationContact } from './types';
import { serializeReservationContact, type ReservationContactDto } from './model.ReservationContact';
import { ModelStateList, modelState } from '@/utils/modelState';
import { cloneRoomBookingState } from './model.Rooms';
import { cloneProgramBookingState, buildSaveReservationRequestProgram } from './model.Programs';
import { RatesRatePlanOverrides } from './Rates/CustomRatePlan';
import { RatesBaseRateOverrides } from './Rates/CustomBaseRate';
import { startOfDay } from 'date-fns';
import { ReservationPersonDto, serializeReservationPerson } from './model.ReservationPerson';
import { toDateString, toDateTimeString } from '@/utils/utils';
import { serializeReservationStatus } from './ReservationStatus';
import { isReservationQuoteStatus, ReservationStatusDto } from './ReservationStatus';
import { ReservationQuoteStatuses } from './ReservationQuoteStatuses/ReservationQuoteStatus';
import { serializeReservationArrivalTransferMode } from './ReservationArrivalTransferMode';
import { serializeReservationDepartureTransferMode } from './ReservationDepartureTransferMode';
import { multiplyAndRemoveDecimal } from '@/utils/utils';
import { sum } from 'lodash';
import { ReservationQuoteDto } from './model.ReservationQuotes';
import { ReservationArrivalTransferDetailsDto, ReservationDepartureTransferDetailsDto } from './model.TransferDetails';
import { IPriceCheckApiService, DefaultPriceCheckApiService } from './interfaces/IPriceCheckApiService';

export { checkPricesWithRatePlanOverride } from "./checkPricesWithRatePlanOverride";
import type { SourceValue } from "./InquirySources/inquirySourceState";

/** Ensures ratePlanFormResponseJson is a valid JSON object string or null. Filters out "[]", empty strings, etc. */
function sanitizeFormResponseJson(value: string | null | undefined): string | null {
    if (!value || typeof value !== 'string') return null;
    const trimmed = value.trim();
    if (!trimmed || !trimmed.startsWith('{')) return null;
    return trimmed;
}

export function buildSaveReservationRequest(reservation: ReservationState): SaveReservationRequest {
    const roomBookings = reservation.RoomBookings.toArray();
    const programBookings = reservation.ProgramBookings.toArray();

    const request: SaveReservationRequest = {
        action: serializeReservationAction(getAction(reservation)),
        createdUtcOverride: reservation.CreatedUtcOverride?.value
            ? toDateTimeString(reservation.CreatedUtcOverride.value)
            : null,
        person: serializeReservationPerson(reservation.Person.value),
        // New unified contact wrapper (Person or Company)
        billingContact: serializeReservationContact((reservation.BillingContact?.value ?? null) as ReservationContact | null),
        rooms: roomBookings.map(rb => buildSaveReservationRequestRoom(rb)),
        programs: programBookings.map(buildSaveReservationRequestProgram),
        vouchers: reservation.AppliedVouchers.value,
        reservationStatus: serializeReservationStatus(reservation.ReservationStatus.value),
        inquirySource: reservation.InquirySource?.Value.value ?? null,
        quote: isReservationQuoteStatus(reservation.ReservationStatus.value) && reservation.Quote.value
            ? {
                expiresAt: toDateTimeString(reservation.Quote.value.ExpiresAt.value),
                status: ReservationQuoteStatuses.serializeReservationQuoteStatus(reservation.Quote.value.Status.value),
                tempReservationEnabled: reservation.Quote.value.TempReservationEnabled.value === true,
                tempReservationReleaseAfter: reservation.Quote.value.TempReservationReleaseAfter?.value
                    ? toDateTimeString(reservation.Quote.value.TempReservationReleaseAfter.value)
                    : null,
            }
            : null,
        arrivalTransferDetails: {
            mode: serializeReservationArrivalTransferMode(reservation.ArrivalTransferDetails.Mode.value)
        },
        departureTransferDetails: {
            mode: serializeReservationDepartureTransferMode(reservation.DepartureTransferDetails.Mode.value)
        },
        ratePlanId: reservation.SelectedRatePlan?.value.RatePlanId,
        ratePlanVersion: reservation.SelectedRatePlan?.value.RatePlanVersion,
        ratePlanFormResponseJson: sanitizeFormResponseJson(reservation.RatePlanFormResponseJson),
        newPeople: reservation.NewPeople.toArray().map(p => p.PersonDto),
        newCompanies: reservation.NewCompanies?.toArray().map(c => c.CompanyDto) ?? [],
        notes: !reservation.ReservationId
            ? reservation.Notes.toArray().map(n => n.NoteText)
            : []
    };

    return request;

}

export function calculateDatePrice(
    breakdown: PriceBreakdownContainer,
    totalBaseAmount: number, totalPayableAmount: number,
    noCharge: boolean): CalculatedPrice {
    if (noCharge) {
        return { breakdown, totalBaseAmount: 0, totalPayableAmount: 0 };
    }

    return {
        breakdown,
        totalBaseAmount,
        totalPayableAmount: totalPayableAmount || 0
    };
}

export function calculateRowPrice(
    breakdowns: PriceBreakdownContainer[],
    totalBaseAmount: number, totalPayableAmount: number,
    noCharge: boolean): CalculatedRowPrice {
    if (noCharge) {
        return { breakdowns, totalBaseAmount: 0, totalPayableAmount: 0 };
    }

    return {
        breakdowns,
        totalBaseAmount,
        totalPayableAmount: totalPayableAmount || 0
    };
}

export function getAmountBeforeDiscountGross(date: RoomBookingDateDto) {
    return (multiplyAndRemoveDecimal(date.PriceBreakdown.AmountBeforeDiscount.Gross) +
        multiplyAndRemoveDecimal(date.MinorAdjustment)) +
        getPriceBreakdownAmountBeforeDiscountGross(date.AdditionalCharges);
}

export function getAmountAfterDiscountGross(date: RoomBookingDateDto) {
    var vouchers = (sum(date.VoucherEffects.map(v => multiplyAndRemoveDecimal(v.Amount))) * -1);
    return (multiplyAndRemoveDecimal(date.PriceBreakdown.AmountAfterDiscount.Gross) +
        multiplyAndRemoveDecimal(date.MinorAdjustment) +
        vouchers +
        getPriceBreakdownAmountAfterDiscountGross(date.AdditionalCharges));
}

export function getPriceBreakdownAmountBeforeDiscountGross(charges: PriceBreakdownContainer[]) {
    let result = 0;

    for (let i = 0; i < charges.length; i++) {
        const amountBeforeDiscountGross = multiplyAndRemoveDecimal(charges[i].AmountBeforeDiscount.Gross);
        result += amountBeforeDiscountGross;
    }

    return result;
}

export function getPriceBreakdownAmountAfterDiscountGross(charges: PriceBreakdownContainer[]) {
    let result = 0;

    for (let i = 0; i < charges.length; i++) {
        const amountAfterDiscountGross = multiplyAndRemoveDecimal(charges[i].AmountAfterDiscount.Gross);
        result += amountAfterDiscountGross;
    }

    return result;
}

export interface SaveReservationRequest {
    action: ReservationActionDto;
    /**
     * Override the created UTC timestamp for locked revenue adjustments.
     * When set, this will be used as the effective date for revenue changes
     * on dates before the revenue lock date. ISO 8601 string format.
     */
    createdUtcOverride: string | null;
    person: ReservationPersonDto;
    billingContact: ReservationContactDto | null;
    rooms: SaveReservationRequestRoom[];
    programs: SaveReservationRequestProgram[];
    vouchers: SaveReservationRequestVoucher[];
    reservationStatus: ReservationStatusDto;
    inquirySource?: SourceValue | null;
    quote: ReservationQuoteDto | null;
    arrivalTransferDetails: ReservationArrivalTransferDetailsDto;
    departureTransferDetails: ReservationDepartureTransferDetailsDto;
    ratePlanId: string;
    ratePlanVersion: number;
    ratePlanFormResponseJson: string | null;
    newPeople: PersonDto[];
    newCompanies: CompanyDto[];
    notes: string[];
}

export interface SaveReservationRequestRoom {
    reservationRoomId: string;
    roomType: string;
    roomLocationId: string | null;
    checkIn: string;
    checkOut: string;
    adults: number;
    children: number;
    infants: number;
    people: SaveReservationRequestRoomPerson[];
    splitFromReservationRoomId: string | null;
    noShow: boolean;
    noCharge: boolean;
    dates: SaveReservationRequestDate[];
    ratePlanOverride: RatesRatePlanOverrides.RatePlanOverrideDto | null;
    baseRateOverride: RatesBaseRateOverrides.BaseRateOverrideDto | null;
    recalculateStayPeriod: boolean | null;
    useOriginalRatePlan: boolean | null;
    vouchers: SaveReservationRequestVoucher[];
    lockRate: boolean;
    recalculate: boolean;
    splitMode: RoomSplitMode | null;
    mergeSourceRoomIds: string[] | null;
    roomFormResponseJson: string | null;
    openPrice: number | null;
    openPriceDescription: string | null;
    ignoreVouchers: boolean;
}

interface SaveReservationRequestRoomPerson {
    reservationRoomPersonId: string;
    person: ReservationPersonDto;
    checkIn: string;
    checkOut: string;
}

interface SaveReservationRequestProgram {
    reservationProgramId: string;
    programType: string;
    start: string;
    end: string;
    people: SaveReservationRequestProgramPerson[];
    splitFromReservationProgramId: string | null;
    dates: SaveReservationRequestDate[];
    ratePlanOverride: RatesRatePlanOverrides.RatePlanOverrideDto | null;
    baseRateOverride: RatesBaseRateOverrides.BaseRateOverrideDto | null;
    recalculateStayPeriod: boolean | null;
    useOriginalRatePlan: boolean | null;
    vouchers: SaveReservationRequestVoucher[];
    lockRate: boolean;
    recalculate: boolean;
    splitMode: ProgramSplitMode | null;
    openPrice: number | null;
    openPriceDescription: string | null;
    ignoreVouchers: boolean;
}

interface SaveReservationRequestProgramPerson {
    reservationProgramPersonId: string;
    person: ReservationPersonDto;
}

interface SaveReservationRequestDate {
    date: string;
    amount: number | null;
    segmentIndex: number | null;
    ratePlanOverride: RatesRatePlanOverrides.RatePlanOverrideDto | null;
    baseRateOverride: RatesBaseRateOverrides.BaseRateOverrideDto | null;
    recalculateStayPeriod: boolean | null;
    useOriginalRatePlan: boolean | null;
    minorAdjustment: number;
    vouchers: SaveReservationRequestVoucher[];
    /**
     * When shifting dates with locked rates, this tells the backend to use the price
     * from this source date instead of recalculating.
     */
    priceSourceDate: string | null;
    /**
     * Snapshot-injected evaluation session JSON for this day. When present
     * (with useOriginalRatePlan = true), the backend writes this as the day's
     * OriginalEvaluationSessionJson and reprices against it instead of the
     * live rate plan.
     */
    evaluationSessionJson: string | null;
    openPrice: number | null;
    openPriceDescription: string | null;
    ignoreVouchers: boolean | null;
}

export interface SaveReservationRequestVoucher {
    reservationVoucherId: string;
    voucherId: string;
    name: string;
    code: string;
}

export interface ReservationActionDto {
    Type: string;
    Value: ReservationActionValueDto;
}

export interface ReservationActionValueDto {
    //
}

export interface ReservationCreateActionValueDto extends ReservationActionValueDto {
    Person: ReservationPersonDto;
}

export interface ReservationUpdateActionValueDto extends ReservationActionValueDto {
    ReservationId: string;
}

export function serializeReservationAction(action: ReservationAction): ReservationActionDto {
    if (!action) {
        return null;
    }

    if (isReservationCreateAction(action)) {
        return {
            Type: "Create",
            Value: {
                Person: serializeReservationPerson(action.Person)
            }
        };
    }

    if (isReservationUpdateAction(action)) {
        return {
            Type: "Update",
            Value: {
                ReservationId: action.ReservationId
            }
        };
    }

    throw new Error("Invalid action type");
}

export const ReservationAction = Symbol("ReservationAction");
export interface ReservationAction {
    [ReservationAction]: boolean;
}

export const ReservationCreateAction = Symbol("ReservationCreateAction");
export interface ReservationCreateAction extends ReservationAction {
    [ReservationCreateAction]: boolean;
    Person: ReservationPerson;
}

export function isReservationCreateAction(val: ReservationAction): val is ReservationCreateAction {
    return val && (val as ReservationCreateAction)[ReservationCreateAction] === true;
}

export const ReservationUpdateAction = Symbol("ReservationUpdateAction");
export interface ReservationUpdateAction extends ReservationAction {
    [ReservationUpdateAction]: boolean;
    ReservationId: string;
}

export function isReservationUpdateAction(val: ReservationAction): val is ReservationUpdateAction {
    return val && (val as ReservationUpdateAction)[ReservationUpdateAction] === true;
}

function getAction(reservation: ReservationState) {
    if (reservation.ReservationId) {
        const action: ReservationUpdateAction = {
            [ReservationAction]: true,
            [ReservationUpdateAction]: true,
            ReservationId: reservation.ReservationId
        };
        return action;
    }

    const action: ReservationCreateAction = {
        [ReservationAction]: true,
        [ReservationCreateAction]: true,
        Person: reservation.Person.value
    };

    return action;
}

export function buildSaveReservationRequestRoom(rb: RoomBookingState): SaveReservationRequestRoom {
    return {
        reservationRoomId: rb.ReservationRoomId,
        roomType: rb.RoomType.value,
        roomLocationId: rb.RoomLocationId.value === 'unassigned' ? null : rb.RoomLocationId.value,
        checkIn: toDateTimeString(rb.CheckIn.value),
        checkOut: toDateTimeString(rb.CheckOut.value),
        adults: rb.Adults.value,
        children: rb.Children.value,
        infants: rb.Infants.value,
        people: rb.People.toArray()
            .filter(p => p.Person.value)
            .map(p => ({
                reservationRoomPersonId: p.ReservationRoomPersonId,
                person: serializeReservationPerson(p.Person.value),
                checkIn: toDateTimeString(p.CheckIn.value),
                checkOut: toDateTimeString(p.CheckOut.value)
            })),
        splitFromReservationRoomId: rb.SplitFromReservationRoomId,
        noShow: rb.NoShow.value,
        noCharge: rb.NoCharge.value,
        dates: rb.Dates.map(d => ({
            date: toDateString(d.Date),
            amount: null as number | null, // Explicitly type null
            segmentIndex: d.SegmentIndex.value,
            recalculateStayPeriod: d.RecalculateStayPeriod.value,
            ratePlanOverride: RatesRatePlanOverrides.serializeRatePlanOverride(d.RatePlanOverride.value),
            baseRateOverride: RatesBaseRateOverrides.serializeBaseRateOverride(d.BaseRateOverride.value),
            useOriginalRatePlan: d.UseOriginalRatePlan.value,
            minorAdjustment: d.MinorAdjustment.value,
            vouchers: d.AppliedVouchers.value,
            priceSourceDate: d.PriceSourceDate?.value ? toDateString(d.PriceSourceDate.value) : null,
            evaluationSessionJson: d.EvaluationSessionJson?.value ?? null,
            openPrice: d.OpenPrice.value,
            openPriceDescription: d.OpenPriceDescription.value,
            ignoreVouchers: d.IgnoreVouchers.value
        })),
        ratePlanOverride: RatesRatePlanOverrides.serializeRatePlanOverride(rb.RatePlanOverride.value),
        baseRateOverride: RatesBaseRateOverrides.serializeBaseRateOverride(rb.BaseRateOverride.value),
        recalculateStayPeriod: rb.RecalculateStayPeriod.value,
        useOriginalRatePlan: rb.UseOriginalRatePlan.value,
        vouchers: rb.AppliedVouchers.value,
        lockRate: rb.LockRate.value,
        recalculate: rb.Recalculate.value,
        splitMode: (rb as any).SplitMode || null,
        mergeSourceRoomIds: (rb as any).MergeSourceRoomIds || null,
        roomFormResponseJson: rb.RoomFormResponseJson.value,
        openPrice: rb.OpenPrice.value,
        openPriceDescription: rb.OpenPriceDescription.value,
        ignoreVouchers: rb.IgnoreVouchers.value
    };
}
